import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PlayerName, CatcherWristbandHeatMap, PITCH_TYPES, Icon } from 'yankees-react-utils';
import { HeatMaps, useOktaQuery } from '../../../../data';
import useConfig from '../../../../utils/useConfig';
import ReportNote from '../../../../components/ReportNote';
import CatcherWristbandBatterStats from './CatcherWristbandBatterStats';
import useOpposingTeamSelector from '../../../../utils/useOpposingTeamSelector';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import miniLhb from '../../../../images/generic_mini_lhb.gif';
import miniRhb from '../../../../images/generic_mini_rhb.gif';

const CatcherWristbandBatterRow = ({ name, bats, throws, pitcherId, batterId, batterNameClass,
    isCatcherPerspective, repertoire, ctwb, data, vsLevelId, useSpanish, noteHasSpanishCallback }) => {
    const opposingTeam = useOpposingTeamSelector();
    const userPlayer = usePlayerSelector();
    const { t: translate } = useTranslation();
    const { CurrentYear } = useConfig();
    const { data: heatmaps } = useOktaQuery(
        ['catcher-wristband', pitcherId, batterId, CurrentYear],
        (t) => HeatMaps.catcherWristband(t, pitcherId, batterId, CurrentYear, { nyyteamid: userPlayer.TeamID, vsteamid: opposingTeam.TeamID }),
    );
    const { earlyOverrides, lateOverrides } = (ctwb?.hitters && ctwb.hitters[batterId]) || {};
    const leads = data?.leads.find((x) => x.playerid === batterId);
    const rispExpand = data?.rispexpand.find((x) => x.playerid === batterId);
    const firstPitches = data?.spgp.filter((x) => x.batterid === batterId && !!x.pitchtype).map((x) => {
        const pitchType = PITCH_TYPES(translate).find((p) => p.id === x.pitchtype);
        return { ...x, pitchTypeName: pitchType.abbr, sortOrder: pitchType.sortorder };
    });
    if (firstPitches?.length) {
        const [{ challpred: chprediction, swallpred: swprediction, msallpred: msprediction }] = firstPitches;
        firstPitches.push({ pitchTypeName: translate('common.all'), sortOrder: -1, chprediction, swprediction, msprediction });
    }

    const noteSpanishDataCallback = (callbackdata) => {
        if (noteHasSpanishCallback) {
            noteHasSpanishCallback(callbackdata);
        }
    };

    return (
        <tr className="ctwbHitterRow">
            <td className={batterNameClass}>
                <PlayerName name={name} bats={bats} />
            </td>
            <td>
                {firstPitches?.length > 0 && <CatcherWristbandBatterStats repertoire={repertoire} rispExpand={rispExpand} firstPitches={firstPitches} />}
            </td>
            <td style={{ position: 'relative' }}>
                {((rispExpand?.ciflag_atk != null && rispExpand?.ciflag_atk !== 0) || (rispExpand?.ciflag_2k != null && rispExpand?.ciflag_2k !== 0)) && (
                    <div className="flaglabel ctwbcelltop">
                        {rispExpand?.ciflag_atk != null && rispExpand?.ciflag_atk !== 0 && <div className={`ciflag${rispExpand.ciflag_atk}`}>AC</div>}
                        {rispExpand?.ciflag_2k != null && rispExpand?.ciflag_2k !== 0 && <div className={`ciflag${rispExpand.ciflag_2k}`}>2K</div>}
                    </div>
                )}
                {rispExpand?.hbpindicator != null && rispExpand?.hbpindicator !== 0 && <div className={`flaglabel ctwbcellbottom hbpflag${rispExpand.hbpindicator}`}><Icon icon="certificate" color={rispExpand.hbpindicator === 2 ? '#FF0000' : '#FFA500'} /></div>}
                {rispExpand?.expand === 1 && <div className="flaglabel">{translate('baseball.abbr-runners-in-position')}</div>}
                <div className="flaglabel">
                    {leads?.over_perc >= 0.2 && <>*</>}{leads?.over_perc >= 0.3 && <>*</>}{leads?.over_perc > 0.4 && <>*</>}
                </div>
                {(isCatcherPerspective ? (bats === 1 || (bats === 3 && throws === 2)) : (bats === 2 || (bats === 3 && throws === 1))) && (
                    <img src={miniLhb} alt="lhb" className="ctwbBatterImg" />
                )}
            </td>
            {repertoire.map((r) => {
                const overrides = earlyOverrides && earlyOverrides[r.FXType] && earlyOverrides[r.FXType][r.FXSubType];
                const ctwbImg = heatmaps?.find((h) => h.PitchType === r.FXType && (h.FxSubType === r.FXSubType || (h.FxSubType === null && r.FXSubType === 0)) && h.Split === 'Early');
                return (
                    <td key={r.PitchNumber} className="wristbandCell_heatmap">
                        <CatcherWristbandHeatMap
                            is2K={false}
                            grade={ctwbImg ? overrides?.val : null}
                            src={ctwbImg ? `data:${ctwbImg.ContentType};base64,${ctwbImg.HeatMapImg}` : undefined}
                            redX={overrides?.RedX}
                            selected={ctwbImg ? overrides?.Selected : null}
                            otz={ctwbImg ? overrides?.OTZ : null}
                            kFree={ctwbImg ? overrides?.KFree : null}
                            greenBox={ctwbImg ? overrides?.GreenBox : null}
                            dashGreenBox={ctwbImg ? overrides?.DashGreenBox : null}
                            isCatcherPerspective={isCatcherPerspective}
                            size={70}
                        />
                    </td>
                );
            })}
            {repertoire.map((r) => {
                const overrides = lateOverrides && lateOverrides[r.FXType] && lateOverrides[r.FXType][r.FXSubType];
                const ctwbImg = heatmaps?.find((h) => h.PitchType === r.FXType && (h.FxSubType === r.FXSubType || (h.FxSubType === null && r.FXSubType === 0)) && h.Split === 'TwoStrikes');
                return (
                    <td key={r.PitchNumber} className="wristbandCell_heatmap">
                        <CatcherWristbandHeatMap
                            is2K
                            grade={ctwbImg ? overrides?.val : null}
                            src={ctwbImg ? `data:${ctwbImg.ContentType};base64,${ctwbImg.HeatMapImg}` : undefined}
                            redX={overrides?.RedX}
                            selected={ctwbImg ? overrides?.Selected : null}
                            otz={ctwbImg ? overrides?.OTZ : null}
                            kFree={ctwbImg ? overrides?.KFree : null}
                            greenBox={ctwbImg ? overrides?.GreenBox : null}
                            dashGreenBox={ctwbImg ? overrides?.DashGreenBox : null}
                            isCatcherPerspective={isCatcherPerspective}
                            size={70}
                        />
                    </td>
                );
            })}
            <td style={{ position: 'relative' }}>
                {(!isCatcherPerspective ? (bats === 1 || (bats === 3 && throws === 2)) : (bats === 2 || (bats === 3 && throws === 1))) && (
                    <img src={miniRhb} alt="rhb" className="ctwbBatterImg" />
                )}
            </td>
            <td style={{ minWidth: 110 }}>
                <ReportNote type="spgp" section={1} batterId={batterId} pitcherId={pitcherId} levelId={(vsLevelId === 8 ? 8 : 7)} showSpanish={useSpanish} doesHaveSpanishDataCallback={noteSpanishDataCallback} />
            </td>
        </tr>
    );
};

CatcherWristbandBatterRow.propTypes = {
    name: PropTypes.string.isRequired,
    bats: PropTypes.number.isRequired,
    throws: PropTypes.number.isRequired,
    pitcherId: PropTypes.number.isRequired,
    batterId: PropTypes.number.isRequired,
    batterNameClass: PropTypes.string,
    isCatcherPerspective: PropTypes.bool,
    repertoire: PropTypes.arrayOf(PropTypes.shape({
        FXType: PropTypes.number.isRequired,
    })).isRequired,
    ctwb: PropTypes.shape({
        hitters: PropTypes.shape({}).isRequired,
    }),
    data: PropTypes.shape({
        leads: PropTypes.arrayOf(PropTypes.shape({
            playerid: PropTypes.number.isRequired,
        })).isRequired,
        rispexpand: PropTypes.arrayOf(PropTypes.shape({
            playerid: PropTypes.number.isRequired,
        })).isRequired,
        spgp: PropTypes.arrayOf(PropTypes.shape({
            pitchtype: PropTypes.number,
            batterid: PropTypes.number.isRequired,
            challpred: PropTypes.number.isRequired,
            swallpred: PropTypes.number.isRequired,
            msallpred: PropTypes.number.isRequired,
        })).isRequired,
    }),
    vsLevelId: PropTypes.number,
    useSpanish: PropTypes.bool,
    noteHasSpanishCallback: PropTypes.func,
};
CatcherWristbandBatterRow.defaultProps = {
    batterNameClass: undefined,
    ctwb: undefined,
    data: undefined,
    isCatcherPerspective: false,
    vsLevelId: undefined,
    useSpanish: false,
    noteHasSpanishCallback: undefined,
};

export default CatcherWristbandBatterRow;
